/**
 * useRitmonitorProgress — Planning board için PostNL Ritmonitor snapshot'ını
 * polling ile çekip route_code → live progress mapping döner.
 *
 * Backend cron her 5 dk'da bir snapshot güncelliyor; UI 60 sn'de bir kontrol
 * eder (sunucuya gereksiz yük yapmamak için).
 *
 * Returns:
 *   {
 *     byCode: Map<string, {ritnaam, chauffeur, lastAction, stops, remaining, donePct, isStale}>,
 *     capturedAt: ISO|null,
 *     ageSec: number|null,
 *   }
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/auth';

export function useRitmonitorProgress({ enabled = true, intervalMs = 60_000 } = {}) {
  const [snapshot, setSnapshot] = useState(null);
  const [now, setNow] = useState(Date.now());
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/postnl/ritmonitor');
        if (cancelled) return;
        // Yeni format: {tabs: [{credential_id, credential_label, snapshot}]}
        // Tüm firma'ların ride'larını birleştir; en yeni captured_at'ı al.
        const tabs = res.data?.data?.tabs || [];
        let allRides = [];
        let latestCaptured = null;
        for (const t of tabs) {
          const snap = t.snapshot;
          if (!snap) continue;
          if (!latestCaptured || (snap.captured_at && snap.captured_at > latestCaptured)) {
            latestCaptured = snap.captured_at;
          }
          for (const r of snap.rides || []) {
            allRides.push({ ...r, _credential_label: t.credential_label });
          }
        }
        setSnapshot(latestCaptured ? { captured_at: latestCaptured, rides: allRides } : null);
      } catch {
        // sessiz — feature degradable
      }
    };
    load();
    timerRef.current = setInterval(load, intervalMs);
    const tick = setInterval(() => setNow(Date.now()), 30_000);
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
      clearInterval(tick);
    };
  }, [enabled, intervalMs]);

  const byCode = useMemo(() => {
    const m = new Map();
    if (!snapshot?.rides) return m;
    // Aynı veriyi birden fazla anahtarla map'le ki kullanıcının route_code
    // formatı ne olursa olsun yakalansın:
    //   ritnaam "0510 STT5 Godsheide 29" →
    //     "0510" (tam ilk token)
    //     "510"  (leading-zero stripped)
    //     "STT5" (ikinci token, depot kodu) – opsiyonel
    const put = (key, value) => {
      if (!key) return;
      const k = String(key).trim();
      if (!k) return;
      if (!m.has(k)) m.set(k, value);
    };
    for (const r of snapshot.rides) {
      const tokens = (r.ritnaam || '').trim().split(/\s+/).filter(Boolean);
      if (!tokens.length) continue;
      const stops = Number(r.aantal_stops) || 0;
      const remaining = Number(r.stops_te_doen) || 0;
      const done = Math.max(0, stops - remaining);
      const entry = {
        ritnaam: r.ritnaam,
        chauffeur: r.chauffeur,
        lastAction: r.tijdstip_laatste_actie,
        stops,
        remaining,
        done,
        donePct: stops > 0 ? Math.round((done / stops) * 100) : 0,
      };
      // 1) ilk token: "0510" / "0712"
      put(tokens[0], entry);
      // 2) numeric leading-zero stripped: "0510" → "510", "0712" → "712"
      if (/^\d+$/.test(tokens[0])) {
        const stripped = String(parseInt(tokens[0], 10));
        put(stripped, entry);
      }
      // 3) ikinci token (örn. "STT5" / "STT2") — yedek eşleşme
      if (tokens.length > 1) put(tokens[1], entry);
    }
    return m;
  }, [snapshot]);

  const ageSec = snapshot?.captured_at
    ? Math.floor((now - new Date(snapshot.captured_at).getTime()) / 1000)
    : null;
  const isStale = ageSec != null && ageSec > 6 * 60; // >6dk → arka plan job kaçırmış

  return {
    byCode,
    capturedAt: snapshot?.captured_at || null,
    ageSec,
    isStale,
  };
}
